import { css, html, nothing } from "lit";

import { customElement, state } from "lit/decorators.js";

import { DataQueryController } from "ui-common";
import { SignalElement, inject } from "ui-common/di";
import type { IAuthState } from "ui-common/services/auth/types";

import "../components/accordion/accordion";
import type { AccordionItem } from "../components/accordion/accordion.ts";
import "../components/change-email-modal/change-email-modal.ts";
import "../components/change-handle-modal/change-handle-modal.ts";
import "../components/page-content/page-content";
import { profileDataQueryOptions } from "../queries/profile-data.ts";
import { getMySubscriptionTierQueryOptions } from "ui-common/queries/subscription-tier";
import type { GetMySubscriptionTierResult } from "api-contracts/rpc/get-my-subscription-tier";
import { rpc } from "ui-common/services/rpc";
import { t } from "../translations/i18n";
import { featureFlag } from "ui-components/wow-feature/wow-feature";
import { featureFlagsQueryOptions } from "../queries/feature-flags";

// Helper function to format price in cents to EUR display string
const formatPrice = (cents: number): string => `€${(cents / 100).toFixed(2)}`;

import "@phosphor-icons/webcomponents/PhPencilSimple";
import { navigateTo } from "../router.ts";
import { deleteUserMutation, updateProfileMutation } from "../services/profile-mutations.ts";
import { NotificationService } from "ui-components/notification/notification-service.ts";

enum ModalNames {
  ChangeHandle = "change-handle",
  ChangeEmail = "change-email",
  DeleteAccount = "delete-account",
}

@customElement("settings-page")
export class SettingsPage extends SignalElement {
  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  static override styles = css`
    .page-container {
      overflow: auto;
    }

    .contact-box {
      padding: var(--sds-size-space-800);
      border-radius: var(--sds-size-radius-600);
      border: 1px solid var(--sds-color-border-default-default);
      margin: var(--sds-size-space-400) 0;
      color: var(--sds-color-text-default-secondary);

      p {
        white-space: pre-wrap;
        margin: 0 0 var(--sds-size-space-600);
      }
    }

    .input-with-button {
      display: flex;
      gap: var(--sds-size-space-200);
      align-items: center;

      wow-input {
        flex: 1;
      }
    }
  `;

  private profileDataQuery = new DataQueryController(this, () => profileDataQueryOptions);

  private subscriptionTierQuery = new DataQueryController<GetMySubscriptionTierResult>(this, () =>
    getMySubscriptionTierQueryOptions(),
  );

  private featureFlagsQuery = new DataQueryController(this, () => featureFlagsQueryOptions);

  @state()
  private modalName: ModalNames | undefined;

  @state()
  private isEditingPrice = false;

  @state()
  private newPriceEuros = "";

  @state() isModalSubmitting = false;

  onModalOpen(name: ModalNames) {
    this.modalName = name;
  }

  private onCloseModal() {
    this.modalName = undefined;
  }

  private async onHandleChanged(e: CustomEvent) {
    try {
      const newHandle = e.detail.newHandle;
      await updateProfileMutation.mutate({ handle: newHandle });
      NotificationService.info(`Handle changed successfully to ${newHandle}`);

      // Refetch after updating handle
      this.profileDataQuery.refetch();
    } catch (error) {
      NotificationService.error(`${error}`);
      console.error("Error saving profile:", error);
    } finally {
      this.onCloseModal();
    }
  }

  private openDeleteAccountModal() {
    this.modalName = ModalNames.DeleteAccount;
  }

  private async deleteAccount() {
    try {
      this.isModalSubmitting = true;
      const userId = this.authState.currentUser.get()?.id ?? "";
      await deleteUserMutation.mutate({ userId });
      navigateTo("sign-in");
    } catch (error) {
      console.error("Error deleting account: ", error);
      NotificationService.error(`Error deleting account: ${error}`);
    } finally {
      this.isModalSubmitting = false;
    }
  }

  private startEditingPrice() {
    const currentPriceCents = this.subscriptionTierQuery.result.data?.tier?.monthlyPriceCents;
    if (currentPriceCents) {
      this.newPriceEuros = (currentPriceCents / 100).toFixed(2);
    }
    this.isEditingPrice = true;
  }

  private cancelEditingPrice() {
    this.isEditingPrice = false;
    this.newPriceEuros = "";
  }

  private async savePrice() {
    try {
      const priceCents = Math.round(parseFloat(this.newPriceEuros) * 100);

      if (priceCents < 100 || priceCents > 99999) {
        NotificationService.error("Price must be between €1.00 and €999.99");
        return;
      }

      await rpc("update-subscription-tier", {
        newMonthlyPriceCents: priceCents,
      });

      NotificationService.info(
        `Subscription price updated to ${formatPrice(priceCents)}. New subscribers will pay this price.`,
      );

      // Refetch subscription tier data
      this.subscriptionTierQuery.refetch();

      this.isEditingPrice = false;
      this.newPriceEuros = "";
    } catch (error) {
      NotificationService.error(`Failed to update price: ${error}`);
      console.error("Error updating subscription price:", error);
    }
  }

  private renderPremiumAccessSection() {
    const { data: tierData } = this.subscriptionTierQuery.result;
    const hasTier = !!tierData?.tier;
    const currentPriceCents = tierData?.tier?.monthlyPriceCents;

    if (!hasTier) {
      return html`
        <div class="contact-box">
          <h3>Premium Access</h3>
          <p>You haven't set up premium content yet. Create your first premium post or link to enable subscriptions.</p>
        </div>
      `;
    }

    if (this.isEditingPrice) {
      return html`
        <div class="contact-box">
          <h3>Premium Access</h3>
          <p>Monthly Subscription Price</p>
          <div class="input-with-button">
            <wow-input
              type="number"
              placeholder="9.99"
              step="0.01"
              min="1"
              max="999.99"
              .value=${this.newPriceEuros}
              @input=${(e: Event) => (this.newPriceEuros = (e.target as HTMLInputElement).value)}
            >
            </wow-input>
            <wow-button size="medium" @click=${this.savePrice}>Save</wow-button>
            <wow-button size="medium" variant="secondary" @click=${this.cancelEditingPrice}>Cancel</wow-button>
          </div>
          <p style="font-size: 0.875rem; margin-top: 0.5rem; color: var(--sds-color-text-default-secondary);">
            Note: Existing subscribers will keep their current price. Only new subscribers will pay the new price.
          </p>
        </div>
      `;
    }

    return html`
      <div class="contact-box">
        <h3>Premium Access</h3>
        <p>Monthly Subscription Price</p>
        <div class="input-with-button">
          <wow-input placeholder="Price" value=${formatPrice(currentPriceCents!)} disabled></wow-input>
          <wow-button size="medium" @click=${this.startEditingPrice}>
            <ph-pencil-simple size="18" class="sort-icon"></ph-pencil-simple>
          </wow-button>
        </div>
      </div>
    `;
  }

  protected override render() {
    const { data: profileData, isPending, error } = this.profileDataQuery.result;

    if (isPending) {
      return html`
        <page-content pageTitle=${t("pages.accountSettings")}>
          <div class="loading">${t("accountSettings.loadingSettings")}</div>
        </page-content>
      `;
    }

    if (error) {
      return html`
        <page-content pageTitle=${t("pages.accountSettings")}>
          <div class="error">${t("accountSettings.errorLoadingSettings", { error: error.message })}</div>
        </page-content>
      `;
    }

    if (!profileData?.email && (!profileData?.handle || profileData?.handle === "")) {
      navigateTo("sign-up", true, ["themes"]);
      return;
    }

    const mailto = "mailto:support@worldofwow.com?subject=Inquiry about WOW&body=I have a question about WOW";

    return html` <page-content pageTitle=${t("pages.accountSettings")}>
      <section class="page-container">
        <div class="input-with-button">
          <wow-input placeholder=${t("accountSettings.email")} value=${profileData?.email} readonly></wow-input>
          <wow-button size="medium" @click=${() => this.onModalOpen(ModalNames.ChangeEmail)}>
            <ph-pencil-simple size="18" class="sort-icon"></ph-pencil-simple>
          </wow-button>
        </div>
        ${profileData.handle
          ? html` <div class="input-with-button">
              <wow-input placeholder=${t("accountSettings.handle")} value=${profileData?.handle} readonly></wow-input>
              <wow-button size="medium" @click="${() => this.onModalOpen(ModalNames.ChangeHandle)}">
                <ph-pencil-simple size="18" class="sort-icon"></ph-pencil-simple>
              </wow-button>
            </div>`
          : nothing}
        ${featureFlag(
          this.featureFlagsQuery.result?.data?.features?.includes("subscription") ?? false,
          this.renderPremiumAccessSection(),
        )}

        <div class="contact-box">
          <p>${t("accountSettings.help")}</p>
          <wow-button href=${mailto}>${t("accountSettings.contactButton")}</wow-button>
        </div>

        <h3>${t("accountSettings.faqsTitle")}</h3>

        <wow-accordion .items=${t("accountSettings.faqs") as AccordionItem[]}></wow-accordion>

        <div class="contact-box">
          <wow-button variant="danger" size="small" full-width @click=${this.openDeleteAccountModal}>
            ${t("accountSettings.deleteAccountModal.buttonLabel")}
          </wow-button>
        </div>

        ${this.renderFragements()}
      </section>
    </page-content>`;
  }

  private renderFragements() {
    switch (this.modalName) {
      case ModalNames.ChangeHandle:
        return html`<change-handle-modal
          @close=${this.onCloseModal}
          @handle-changed=${this.onHandleChanged}
        ></change-handle-modal>`;
      case ModalNames.ChangeEmail: {
        const { data: profileData, isPending, error } = this.profileDataQuery.result;
        if (isPending || error) return nothing;
        return html`<change-email-modal
          .userEmail=${profileData?.email ?? ""}
          @close=${this.onCloseModal}
        ></change-email-modal>`;
      }
      case ModalNames.DeleteAccount: {
        const modalTitle = t("accountSettings.deleteAccountModal.title");
        const submitButtonText = this.isModalSubmitting
          ? t("accountSettings.deleteAccountModal.deleting")
          : t("accountSettings.deleteAccountModal.delete");
        return html`
          <wow-modal
            variant="overlay"
            @close=${this.onCloseModal}
            @footer-button-clicked=${this.deleteAccount}
            ?disable-primary-button=${this.isModalSubmitting}
          >
            <span slot="header">${modalTitle}</span>
            <span slot="footer-button-text">${submitButtonText}</span>

            <div class="modal-conten">${t("accountSettings.deleteAccountModal.msg")}</div>
          </wow-modal>
        `;
      }
      default:
        return nothing;
    }
  }
}
